import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { useScheduleI18n } from '../../i18n'
import type {
  ScheduleEvent,
  ScheduleEventId,
  ScheduleRetrieve,
} from '../../types'
import { dsText, fontSize, shadowVar, tokenVar } from '../../utils'
import {
  getCurrentRoutingState,
  processScheduleEvents,
} from '../../utils/scheduleEvent'
import { OverridesList } from '../OverridesList'
import { ScheduleStatusBadge } from '../ScheduleStatusBadge'
import {
  calendarStyles,
  WeeklyHoursCalendar,
  WeeklyHoursTable,
} from '../WeeklyHoursCalendar'
import { ScheduleInfo } from './ScheduleInfo'

export interface ScheduleDetailProps {
  /** The schedule data */
  schedule: ScheduleRetrieve
  /** Schedule events */
  events: ScheduleEvent[]
  /** Timezone display name */
  timeZoneName?: string
  /** Whether user can create new items */
  canNew?: boolean
  /** Whether user can edit */
  canEdit?: boolean
  /** Whether user can delete */
  canDelete?: boolean
  /** Optional back button element rendered inline with the schedule name */
  backButton?: ReactNode
  /** Called when edit schedule info is clicked */
  onEditInfo?: () => void
  /** Called when delete schedule is clicked */
  onDeleteSchedule?: () => void
  /** Called when a new weekly hours slot is selected */
  onCreateWeeklyHours?: (start: Date, end: Date) => void
  /** Called when weekly hours event is changed */
  onUpdateWeeklyHours?: (
    eventId: ScheduleEventId,
    start: Date,
    end: Date,
  ) => void
  /** Called when weekly hours event is deleted */
  onDeleteWeeklyHours?: (eventId: ScheduleEventId) => void
  /** Called when add override button is clicked */
  onAddOverride?: () => void
  /** Called when add holidays button is clicked */
  onAddHolidays?: () => void
  /** Called when override edit button is clicked */
  onEditOverride?: (eventId: ScheduleEventId) => void
  /** Called when override delete button is clicked */
  onDeleteOverride?: (eventId: ScheduleEventId, eventName: string) => void
  /** Custom edit icon (defaults to built-in pencil icon) */
  editIcon?: ReactNode
  /** Custom delete icon (defaults to built-in trash icon) */
  deleteIcon?: ReactNode
  /** Custom class name */
  className?: string
}

const CalendarHeaderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 2V5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16 2V5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect
      x="3"
      y="4"
      width="18"
      height="17"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
  </svg>
)

/**
 * Schedule detail view
 * Combines the weekly hours calendar and overrides list
 */
export const ScheduleDetail = ({
  schedule,
  events,
  timeZoneName,
  canNew = false,
  canEdit = false,
  canDelete = false,
  backButton,
  onEditInfo,
  onDeleteSchedule,
  onCreateWeeklyHours,
  onUpdateWeeklyHours,
  onDeleteWeeklyHours,
  onAddOverride,
  onAddHolidays,
  onEditOverride,
  onDeleteOverride,
  editIcon,
  deleteIcon,
  className = '',
}: ScheduleDetailProps) => {
  const { text } = useScheduleI18n()
  const [routingTick, setRoutingTick] = useState(Date.now())

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRoutingTick(Date.now())
    }, 30000)

    return () => window.clearInterval(intervalId)
  }, [])

  const routingState = useMemo(() => {
    const helpers = processScheduleEvents(events, schedule.time_zone)

    if (helpers.length === 0 && schedule.switchboard_status?.state) {
      return schedule.switchboard_status.state
    }

    return getCurrentRoutingState(helpers)
  }, [
    events,
    schedule.time_zone,
    schedule.switchboard_status?.state,
    routingTick,
  ])

  const routingTone =
    routingState === 'OPEN'
      ? 'open'
      : routingState === 'HOLIDAY'
        ? 'holiday'
        : 'closed'
  const routingLabel =
    routingState === 'OPEN'
      ? text('schedule.status.open', 'Open')
      : routingState === 'HOLIDAY'
        ? text('schedule.status.holiday', 'Holiday')
        : text('schedule.status.closed', 'Closed')
  const canCreateOrEdit = canNew || canEdit
  const [hoursViewMode, setHoursViewMode] = useState<'visual' | 'table'>(
    'visual',
  )

  return (
    <div className={`schedule-detail ${className}`}>
      {/* Inject calendar styles */}
      <style>{calendarStyles}</style>
      <style>{`
        .schedule-detail__content {
          display: flex;
          flex-wrap: nowrap;
          align-items: flex-start;
          width: 100%;
          gap: 24px;
          padding: 0 16px;
        }

        .schedule-detail__calendar-column {
          flex: 1.2 1 620px;
          min-width: 320px;
        }

        .schedule-detail__overrides-column {
          display: flex;
          flex-direction: column;
          flex: 1 1 420px;
          min-width: 360px;
        }

        @media (max-width: 1360px) {
          .schedule-detail__content {
            flex-direction: column;
            flex-wrap: nowrap;
          }

          .schedule-detail__calendar-column,
          .schedule-detail__overrides-column {
            width: 100%;
            min-width: 0;
            flex: 1 1 auto;
          }
        }
      `}</style>

      <ScheduleInfo
        name={schedule.name}
        description={schedule.description}
        routingStatus={routingState}
        canEdit={canEdit}
        canDelete={canDelete}
        backButton={backButton}
        onEditInfo={onEditInfo}
        onDelete={onDeleteSchedule}
        editIcon={editIcon}
        deleteIcon={deleteIcon}
      />

      <div className="schedule-detail__content">
        {/* Weekly Hours Calendar */}
        <div
          className="schedule-detail__calendar-column"
          style={{
            border: `1px solid ${tokenVar('border.default')}`,
            borderRadius: '8px',
            backgroundColor: tokenVar('bg.default'),
            boxShadow: shadowVar('card'),
            padding: '24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    fontFamily: dsText.Body1.fontFamily,
                    fontSize: fontSize(dsText.Body1.fontSize),
                    fontWeight: dsText.Body1_Bold.fontWeight,
                    lineHeight: dsText.Body1.lineHeight,
                    color: tokenVar('fg.default'),
                  }}
                >
                  {text('schedule.detail.currentRouting', 'Current Routing:')}
                </span>
                <ScheduleStatusBadge label={routingLabel} tone={routingTone} />
              </div>
              {timeZoneName && (
                <span
                  style={{
                    fontFamily: dsText.Body4.fontFamily,
                    fontSize: fontSize(dsText.Body4.fontSize),
                    fontWeight: dsText.Body4.fontWeight,
                    lineHeight: dsText.Body4.lineHeight,
                    letterSpacing: dsText.Body4.letterSpacing,
                    color: tokenVar('fg.subtle'),
                  }}
                >
                  {timeZoneName}
                </span>
              )}
            </div>

            <div
              style={{
                display: 'inline-flex',
                border: `1px solid ${tokenVar('border.default')}`,
                borderRadius: '8px',
                backgroundColor: tokenVar('bg.default'),
                padding: '6px',
                gap: '8px',
              }}
            >
              <button
                type="button"
                onClick={() => setHoursViewMode('visual')}
                style={{
                  height: '32px',
                  minHeight: '32px',
                  minWidth: '32px',
                  padding: '2px 10px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor:
                    hoursViewMode === 'visual'
                      ? tokenVar('bg.muted')
                      : 'transparent',
                  color: tokenVar('fg.muted'),
                  fontFamily: dsText.Body3.fontFamily,
                  fontSize: fontSize(dsText.Body3.fontSize),
                  fontWeight:
                    hoursViewMode === 'visual' ? 500 : dsText.Body3.fontWeight,
                  lineHeight: dsText.Body3.lineHeight,
                  cursor: 'pointer',
                }}
              >
                {text('schedule.detail.hoursView.calendar', 'Calendar')}
              </button>
              <button
                type="button"
                onClick={() => setHoursViewMode('table')}
                style={{
                  height: '32px',
                  minHeight: '32px',
                  minWidth: '32px',
                  padding: '2px 10px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor:
                    hoursViewMode === 'table'
                      ? tokenVar('bg.muted')
                      : 'transparent',
                  color: tokenVar('fg.muted'),
                  fontFamily: dsText.Body3.fontFamily,
                  fontSize: fontSize(dsText.Body3.fontSize),
                  fontWeight:
                    hoursViewMode === 'table' ? 500 : dsText.Body3.fontWeight,
                  lineHeight: dsText.Body3.lineHeight,
                  cursor: 'pointer',
                }}
              >
                {text('schedule.detail.hoursView.list', 'List')}
              </button>
            </div>
          </div>
          {hoursViewMode === 'visual' ? (
            <WeeklyHoursCalendar
              events={events}
              scheduleTimeZone={schedule.time_zone}
              canEdit={canEdit}
              canDelete={canDelete}
              onSelect={onCreateWeeklyHours}
              onEventChange={onUpdateWeeklyHours}
              onEventDelete={onDeleteWeeklyHours}
            />
          ) : (
            <WeeklyHoursTable
              events={events}
              scheduleTimeZone={schedule.time_zone}
              canEdit={canEdit}
              canDelete={canDelete}
              onCreateSlot={onCreateWeeklyHours}
              onUpdateSlot={onUpdateWeeklyHours}
              onDeleteSlot={onDeleteWeeklyHours}
            />
          )}
        </div>

        <div className="schedule-detail__overrides-column">
          <div
            className="schedule-detail__overrides-card"
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              border: `1px solid ${tokenVar('border.default')}`,
              borderRadius: '8px',
              backgroundColor: tokenVar('bg.default'),
              boxShadow: shadowVar('card'),
              padding: '20px',
            }}
          >
            <OverridesList
              events={events}
              scheduleTimeZone={schedule.time_zone}
              mode="all"
              title={text('schedule.overrides.title', 'Schedule Overrides')}
              titleIcon={<CalendarHeaderIcon />}
              canEdit={canCreateOrEdit}
              canDelete={canDelete}
              onAddOverride={onAddOverride}
              onAddHolidays={onAddHolidays}
              onEditEvent={onEditOverride}
              onDeleteEvent={onDeleteOverride}
              editIcon={editIcon}
              deleteIcon={deleteIcon}
              addOverrideLabel={text(
                'schedule.overrides.actions.addOverride',
                'Add Override',
              )}
              addHolidaysLabel={text(
                'schedule.overrides.actions.addHoliday',
                'Add Holiday',
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
